import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface EmailTemplate {
  id?: number;
  name: string;
  emailSubject: string;
  emailText: string | { [key: string]: any };
  title1?: string;
  content1?: string;
  notificationMessage?: string;
  emailTemplateName: string;
  imageUrl?: string;
  itsDayDate: string;
  scheduleDate?: string;
  createdOn?: string;
  modifiedOn?: string;
}

export interface SendGreetingRequest {
  carIqUserId: string;
  greetingName: string;
  text: string;
  attachment?: File;
}

export interface CarIqUser {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  cellNumber: string;
  isActivated: boolean;
  imageUrl?: string;
  devices?: any[];
  deviceSticker?: any[];
  carIds?: any[];
}

export interface UserSearchResponse {
  pageNum: number;
  noOfRecords: number;
  totalPages: number;
  rows: CarIqUser[];
}

export interface GreetingBroadcastRequest {
  [key: string]: any;
  GreetigName?: string;
  Email?: string;
  NotificationType?: string;
  EmailTemplate?: string;
  emailSubject?: string;
  emailText?: string | { [key: string]: any };
  notificationMessage?: string;
  scheduledDate?: string;
}

export interface GreetingCalendarLoaderRequest {
  name: string;
  emailSubject: string;
  emailText: string;
  notificationMessage: string;
  emailTemplateName: string;
  imageUrl: string;
  scheduleDate: string;
}

export interface LoaderAttributeDefinition {
  name: string;
  description?: string;
  optional?: boolean;
}

export interface LoaderDefinition {
  writtenBy?: string;
  category?: string;
  name: string;
  description?: string;
  supportedRoles?: string[];
  supportedDomains?: string[];
  authorized?: boolean;
  attrDefinition: LoaderAttributeDefinition[];
}

export interface ResponseWithIdJson {
  id: number;
  [key: string]: any;
}

export interface SendNotificationRequest {
  receipent: string;
  subject: string;
  emailTemplateUrl: string;
  templateModel: {
    [key: string]: any;
    userName: string;
    image_url: string;
    notificationMessage?: string;
  };
  cc?: string;
  replyTo?: string;
  scheduledDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GreetingService {
  private baseUrl = 'https://dev2-ecu.mycariq.com/Cariq/greetingAndNotification';
  private adminBaseUrl = 'https://dev2-ecu.mycariq.com/Cariq/admin';
  private emailTemplateBaseUrl = 'https://mycariq.com/email_notification_template';
  private basicAuthHeader = 'Basic SGFyaXNoLkt1a2thbGFAbXljYXJpcS5jb206ZTEwYWRjMzk0OWJhNTlhYmJlNTZlMDU3ZjIwZjg4M2U=';
  private notifyUserAuthHeader = 'Basic bm90aWZ5dXNlcjo5NDJjY2IwZWVhOGE3MDZjNGMzNGExNjg5MWY4NGU3Nw==';

  private readonly vmTemplateUrls: { [name: string]: string } = {
    CommonImageGreeting: `${this.emailTemplateBaseUrl}/common_icici.vm`
  };
  private readonly legacyEmailTextFields = ['title1', 'title2', 'title3', 'content1', 'content2'];

  constructor(private http: HttpClient) { }

  private getAuthHeaders(isFormData = false): HttpHeaders {
    const headersConfig: { [name: string]: string } = {
      Authorization: this.basicAuthHeader,
      Accept: 'application/json'
    };

    if (!isFormData) {
      headersConfig['Content-Type'] = 'application/json';
    }

    return new HttpHeaders(headersConfig);
  }

  private getAuthOptions(isFormData = false): { headers: HttpHeaders } {
    return { headers: this.getAuthHeaders(isFormData) };
  }

  private getNotifyAuthOptions(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        Authorization: this.notifyUserAuthHeader,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      })
    };
  }

  resolveEmailTemplateUrl(templateNameOrUrl: string): string {
    const value = (templateNameOrUrl || '').trim();

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    if (this.vmTemplateUrls[value]) {
      return this.vmTemplateUrls[value];
    }

    const fileName = value.endsWith('.vm') ? value : `${value}.vm`;
    return `${this.emailTemplateBaseUrl}/${fileName}`;
  }

  // Email Template APIs
  createTemplate(template: EmailTemplate): Observable<EmailTemplate> {
    return this.http.post<EmailTemplate>(`${this.baseUrl}/calendar`, this.buildTemplatePayload(template), this.getAuthOptions());
  }

  updateTemplate(id: number, template: EmailTemplate): Observable<EmailTemplate> {
    return this.http.put<EmailTemplate>(`${this.baseUrl}/calendar/${id}`, this.buildTemplatePayload(template), this.getAuthOptions());
  }

  uploadNotificationFile(notificationId: number, file: File): Observable<ResponseWithIdJson> {
    const formData = new FormData();
    const uploadFile = file.type ? file : new File([file], file.name, { type: 'application/octet-stream' });
    formData.append('file', uploadFile, file.name);

    return this.http.post<ResponseWithIdJson>(`${this.baseUrl}/${notificationId}`, formData, this.getAuthOptions(true));
  }

  getAllTemplates(): Observable<EmailTemplate[]> {
    return this.http.get<EmailTemplate[] | { [key: string]: any }>(`${this.baseUrl}/calendar`, this.getAuthOptions())
      .pipe(map(response => this.normalizeTemplateList(response)));
  }

  getTemplateById(id: number): Observable<EmailTemplate> {
    return this.http.get<EmailTemplate>(`${this.baseUrl}/calendar/${id}`, this.getAuthOptions());
  }

  getTemplatesByDate(date: string): Observable<EmailTemplate[]> {
    return this.http.get<EmailTemplate[] | { [key: string]: any }>(`${this.baseUrl}/calendar/${date}`, this.getAuthOptions())
      .pipe(map(response => this.normalizeTemplateList(response)));
  }

  deleteTemplateById(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/calendar/${id}`, this.getAuthOptions());
  }

  deleteTemplateByBody(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/calendar`, {
      ...this.getAuthOptions(),
      body: { id }
    });
  }

  // Send Greeting API
  sendGreetingEmail(carIqUserId: string, greetingName: string, text: string, attachment?: File): Observable<any> {
    const formData = new FormData();
    formData.append('carIqUserId', carIqUserId);
    formData.append('greetingName', greetingName);
    formData.append('text', text);
    if (attachment) {
      formData.append('attachment', attachment);
    }

    return this.http.post(`${this.baseUrl}/sendGreetingMail`, formData, this.getAuthOptions(true));
  }

  // Broadcast Greeting APIs
  sendSingleGreeting(request: GreetingBroadcastRequest): Observable<any> {
    return this.http.post(`${this.adminBaseUrl}/loader/loadSingle/GreetingsAndInformationBroadcaster`, request, this.getAuthOptions());
  }

  sendMultipleGreetings(requests: GreetingBroadcastRequest[]): Observable<any> {
    return this.http.post(`${this.adminBaseUrl}/loader/loadMultiple/GreetingsAndInformationBroadcaster`, requests, this.getAuthOptions());
  }

  sendMultipleScheduledGreetings(requests: GreetingCalendarLoaderRequest[]): Observable<any> {
    return this.http.post(`${this.adminBaseUrl}/loader/loadMultiple/GreetingsAndInformationCalendarLoader`, requests, this.getAuthOptions());
  }

  getGreetingBroadcasterLoaderDefinition(): Observable<LoaderDefinition> {
    return this.http.get<LoaderDefinition>(`${this.adminBaseUrl}/loader/get/GreetingsAndInformationBroadcaster`, this.getAuthOptions());
  }

  sendNotification(request: SendNotificationRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/sendNotification`, request, this.getNotifyAuthOptions());
  }

  sendNotifications(requests: SendNotificationRequest[]): Observable<any[]> {
    return forkJoin(requests.map(request => this.sendNotification(request)));
  }

  // User Search API
  searchUsers(searchKey: string, pageNo: number = 1, pageSize: number = 10): Observable<UserSearchResponse> {
    const url = `https://dev2-ecu.mycariq.com/Cariq/cariqusers/${encodeURIComponent(searchKey)}/${pageNo}/${pageSize}`;
    return this.http.get<UserSearchResponse>(url, this.getAuthOptions());
  }

  private buildTemplatePayload(template: EmailTemplate): EmailTemplate {
    const scheduleDate = template.scheduleDate || template.itsDayDate;
    
    // Convert emailText to JSON object if it's a string
    let emailTextObj: any = {};
    if (typeof template.emailText === 'string') {
      try {
        emailTextObj = JSON.parse(template.emailText);
      } catch {
        emailTextObj = {};
      }
    } else if (typeof template.emailText === 'object') {
      emailTextObj = template.emailText;
    }

    const cleanedEmailText = this.removeEmptyLegacyEmailTextFields(emailTextObj);
    const payload: EmailTemplate = {
      ...template,
      emailText: cleanedEmailText,
      itsDayDate: scheduleDate,
      scheduleDate,
      notificationMessage: template.notificationMessage || '',
      imageUrl: template.imageUrl || ''
    };

    this.legacyEmailTextFields.forEach(field => {
      if (
        cleanedEmailText &&
        typeof cleanedEmailText === 'object' &&
        !Array.isArray(cleanedEmailText) &&
        cleanedEmailText[field] != null &&
        cleanedEmailText[field] !== ''
      ) {
        (payload as any)[field] = cleanedEmailText[field];
      }

      if (payload[field as keyof EmailTemplate] == null || payload[field as keyof EmailTemplate] === '') {
        delete payload[field as keyof EmailTemplate];
      }
    });

    return payload;
  }

  private normalizeTemplateList(response: EmailTemplate[] | { [key: string]: any }): EmailTemplate[] {
    const possibleList = Array.isArray(response)
      ? response
      : response?.rows || response?.data || response?.result || response?.items || response?.calendar || [];

    if (Array.isArray(possibleList)) {
      return possibleList.map(template => this.normalizeTemplate(template));
    }

    if (possibleList && typeof possibleList === 'object') {
      return [this.normalizeTemplate(possibleList)];
    }

    return [];
  }

  private normalizeTemplate(template: any): EmailTemplate {
    const emailText = this.removeEmptyLegacyEmailTextFields(template?.emailText ?? this.buildLegacyEmailText(template));

    return {
      ...template,
      emailText,
      name: template?.name || template?.greetingName || template?.GreetigName || '',
      emailSubject: template?.emailSubject || '',
      emailTemplateName: template?.emailTemplateName || template?.templateName || ''
    };
  }

  private buildLegacyEmailText(template: any): string | { [key: string]: any } {
    if (template?.title1 || template?.content1) {
      return {
        title1: template.title1 || '',
        content1: template.content1 || ''
      };
    }

    return {};
  }

  private removeEmptyLegacyEmailTextFields(emailText: any): string | { [key: string]: any } {
    if (!emailText || typeof emailText !== 'object' || Array.isArray(emailText)) {
      return emailText || {};
    }

    const cleanedEmailText = { ...emailText };

    this.legacyEmailTextFields.forEach(field => {
      if (cleanedEmailText[field] == null || cleanedEmailText[field] === '') {
        delete cleanedEmailText[field];
      }
    });

    return cleanedEmailText;
  }
}
